import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-measure';
import 'leaflet-measure/dist/leaflet-measure.css';
import './MapTools.css';

// UTM conversion functions
const latLngToUtm = (lat: number, lng: number) => {
  const zoneNumber = Math.floor((lng + 180) / 6) + 1;

  // Special zones for Norway
  if (lat >= 56 && lat < 64 && lng >= 3 && lng < 12) {
    // Zone 32V
    return { zone: 32, letter: 'V', ...calculateUtmCoords(lat, lng, 32) };
  }
  if (lat >= 72 && lat < 84) {
    if (lng >= 0 && lng < 9) return { zone: 31, letter: 'X', ...calculateUtmCoords(lat, lng, 31) };
    if (lng >= 9 && lng < 21) return { zone: 33, letter: 'X', ...calculateUtmCoords(lat, lng, 33) };
    if (lng >= 21 && lng < 33) return { zone: 35, letter: 'X', ...calculateUtmCoords(lat, lng, 35) };
    if (lng >= 33 && lng < 42) return { zone: 37, letter: 'X', ...calculateUtmCoords(lat, lng, 37) };
  }

  const letter = getUtmLetter(lat);
  return { zone: zoneNumber, letter, ...calculateUtmCoords(lat, lng, zoneNumber) };
};

const calculateUtmCoords = (lat: number, lng: number, zoneNumber: number) => {
  const a = 6378137;
  const e = 0.081819190842622;
  const e2 = e * e;
  const k0 = 0.9996;

  const latRad = (lat * Math.PI) / 180;
  const lngRad = (lng * Math.PI) / 180;
  const centralMeridian = ((zoneNumber - 1) * 6 - 180 + 3) * (Math.PI / 180);

  const N = a / Math.sqrt(1 - e2 * Math.sin(latRad) * Math.sin(latRad));
  const T = Math.tan(latRad) * Math.tan(latRad);
  const C = (e2 / (1 - e2)) * Math.cos(latRad) * Math.cos(latRad);
  const A = (lngRad - centralMeridian) * Math.cos(latRad);

  const M = a * ((1 - e2 / 4 - 3 * e2 * e2 / 64 - 5 * e2 * e2 * e2 / 256) * latRad -
    (3 * e2 / 8 + 3 * e2 * e2 / 32 + 45 * e2 * e2 * e2 / 1024) * Math.sin(2 * latRad) +
    (15 * e2 * e2 / 256 + 45 * e2 * e2 * e2 / 1024) * Math.sin(4 * latRad) -
    (35 * e2 * e2 * e2 / 3072) * Math.sin(6 * latRad));

  const easting = k0 * N * (A + (1 - T + C) * A * A * A / 6 +
    (5 - 18 * T + T * T + 72 * C - 58 * (e2 / (1 - e2))) * A * A * A * A * A / 120) + 500000;

  let northing = k0 * (M + N * Math.tan(latRad) * (A * A / 2 +
    (5 - T + 9 * C + 4 * C * C) * A * A * A * A / 24 +
    (61 - 58 * T + T * T + 600 * C - 330 * (e2 / (1 - e2))) * A * A * A * A * A * A / 720));

  if (lat < 0) {
    northing += 10000000;
  }

  return {
    easting: Math.round(easting * 100) / 100,
    northing: Math.round(northing * 100) / 100
  };
};

const getUtmLetter = (lat: number): string => {
  const letters = 'CDEFGHJKLMNPQRSTUVWXX';
  const index = Math.floor((lat + 80) / 8);
  return letters.charAt(Math.min(index, letters.length - 1));
};

const MapTools = () => {
  const map = useMap();
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    lat: number;
    lng: number;
  } | null>(null);

  useEffect(() => {
    // Override Leaflet's default panTo behavior during measurements
    const originalPanTo = map.panTo.bind(map);
    const originalSetView = map.setView.bind(map);

    // Add measure control
    const measureControl = new (L.Control as any).Measure({
      position: 'topright',
      primaryLengthUnit: 'meters',
      secondaryLengthUnit: 'kilometers',
      primaryAreaUnit: 'sqmeters',
      secondaryAreaUnit: 'hectares',
      activeColor: '#2563eb',
      completedColor: '#059669',
      captureZIndex: 10000,
      popupOptions: {
        className: 'leaflet-measure-resultpopup',
        autoPan: false,
        autoPanPadding: [0, 0]
      }
    });

    // Listen to measure events
    map.on('measurestart', () => {
      console.log('Measurement started');

      // Override panTo to do nothing during measurement
      map.panTo = function(..._args: any[]) {
        console.log('panTo blocked during measurement');
        return this;
      };

      // Override setView to preserve current view during measurement
      map.setView = function(center: any, zoom?: any, _options?: any) {
        console.log('setView blocked during measurement', center, zoom);
        return this;
      };
    });

    map.on('measurefinish', () => {
      console.log('Measurement finished');
      map.panTo = originalPanTo;
      map.setView = originalSetView;
    });

    map.addControl(measureControl);

    // Add print button
    const printControl = L.Control.extend({
      options: {
        position: 'topright'
      },
      onAdd: function() {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom');
        container.innerHTML = '<button class="map-tool-button" title="Print Map">🖨️</button>';
        container.style.backgroundColor = 'white';
        container.style.width = '34px';
        container.style.height = '34px';
        container.style.cursor = 'pointer';

        container.onclick = function(e: Event) {
          e.stopPropagation();
          window.print();
        };

        return container;
      }
    });

    const printBtn = new printControl();
    map.addControl(printBtn);

    return () => {
      map.off('measurestart');
      map.off('measurefinish');
      map.panTo = originalPanTo;
      map.setView = originalSetView;
      map.removeControl(measureControl);
      map.removeControl(printBtn);
    };
  }, [map]);

  // Context menu for coordinates
  useEffect(() => {
    const handleContextMenu = (e: L.LeafletMouseEvent) => {
      e.originalEvent.preventDefault();
      setContextMenu({
        x: e.originalEvent.clientX,
        y: e.originalEvent.clientY,
        lat: e.latlng.lat,
        lng: e.latlng.lng
      });
    };

    const handleClick = () => {
      setContextMenu(null);
    };

    map.on('contextmenu', handleContextMenu);
    map.on('click', handleClick);

    return () => {
      map.off('contextmenu', handleContextMenu);
      map.off('click', handleClick);
    };
  }, [map]);

  // Close context menu on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setContextMenu(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      alert(`${label} copied to clipboard!`);
      setContextMenu(null);
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    });
  };

  const formatWgs84Decimal = (lat: number, lng: number) => {
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  };

  const formatWgs84Dms = (lat: number, lng: number) => {
    const latDms = decimalToDms(lat, true);
    const lngDms = decimalToDms(lng, false);
    return `${latDms}, ${lngDms}`;
  };

  const decimalToDms = (decimal: number, isLat: boolean) => {
    const absolute = Math.abs(decimal);
    const degrees = Math.floor(absolute);
    const minutesFloat = (absolute - degrees) * 60;
    const minutes = Math.floor(minutesFloat);
    const seconds = ((minutesFloat - minutes) * 60).toFixed(2);

    let direction = '';
    if (isLat) {
      direction = decimal >= 0 ? 'N' : 'S';
    } else {
      direction = decimal >= 0 ? 'E' : 'W';
    }

    return `${degrees}°${minutes}'${seconds}"${direction}`;
  };

  return (
    <>
      {contextMenu && createPortal(
        <div
          className="coordinate-context-menu"
          style={{
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="coordinate-context-menu-item"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(
                formatWgs84Decimal(contextMenu.lat, contextMenu.lng),
                'WGS84 Decimal'
              );
            }}
          >
            <div className="coordinate-context-menu-label">Copy WGS84 (Decimal)</div>
            <div className="coordinate-context-menu-value">
              {formatWgs84Decimal(contextMenu.lat, contextMenu.lng)}
            </div>
          </div>

          <div
            className="coordinate-context-menu-item"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(
                formatWgs84Dms(contextMenu.lat, contextMenu.lng),
                'WGS84 DMS'
              );
            }}
          >
            <div className="coordinate-context-menu-label">Copy WGS84 (DMS)</div>
            <div className="coordinate-context-menu-value">
              {formatWgs84Dms(contextMenu.lat, contextMenu.lng)}
            </div>
          </div>

          <div
            className="coordinate-context-menu-item"
            onClick={(e) => {
              e.stopPropagation();
              const utm = latLngToUtm(contextMenu.lat, contextMenu.lng);
              copyToClipboard(
                `${utm.zone}${utm.letter} ${utm.easting}E ${utm.northing}N`,
                'UTM'
              );
            }}
          >
            <div className="coordinate-context-menu-label">Copy UTM</div>
            <div className="coordinate-context-menu-value">
              {(() => {
                const utm = latLngToUtm(contextMenu.lat, contextMenu.lng);
                return `${utm.zone}${utm.letter} ${utm.easting}E ${utm.northing}N`;
              })()}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default MapTools;
